import React, { useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useHistory } from 'react-router-dom';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ArrowForward from '@mui/icons-material/ArrowForward';
import ActivateIcon from '@mui/icons-material/CheckCircle';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { fetchData, deleteData, activateData } from '../../../apiService';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import SearchIcon from '@mui/icons-material/Search';
import Button from '@mui/material/Button';
import BillPrint from '../sale/Bill';
import PrintIcon from '@mui/icons-material/Print';
import Info from '@mui/icons-material/Info';
import ReactDOM from 'react-dom';
import AddIcon from '@mui/icons-material/Add';
import CopyrightIcon from '@mui/icons-material/Copyright';
import RefreshIcon from '@mui/icons-material/Refresh';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import gender from './gender_looms_logo.png';

function NewSaleIndex() {
    const [data, setData] = useState([]);
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const history = useHistory(); // Access the navigation history
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [isFocused, setIsFocused] = useState(false);

    const { isAuthenticated, companyId, branchId, permissions, token, logout } = useAuth();
    const hasCreateMasterPermission = permissions.includes('Create Transaction');
    const hasEditMasterPermission = permissions.includes('Edit Transaction');
    const hasDeleteMasterPermission = permissions.includes('Delete Transaction');

    useEffect(() => {
        // Check if the token is expired
        if (isAuthenticated && tokenIsExpired(token)) {
            // Token is expired, log out the user
            logout();
            return;
        }
        setFromDate(getFormattedStartDate());
        setToDate(getFormattedCurrentDate());
        // Token is not expired, fetch data
        fetchTableData();
    }, [isAuthenticated, companyId, branchId, token, logout]);

    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);

    //handle search
    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
    };

    const toggleSearchInput = () => {
        setShowSearchInput(!showSearchInput);
    };

    //search
    const filteredData = data.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const getFormattedCurrentDate = () => {
        const currentDate = new Date();
        return currentDate.toISOString().split('T')[0];
    };

    const getFormattedStartDate = () => {
        const monthStartDate = new Date();
        monthStartDate.setDate(1);
        return monthStartDate.toISOString().split('T')[0];
    };

    const handleFromDateChange = (e) => {
        const selectedDate = e.target.value;
        setFromDate(selectedDate);
    };

    const handleToDateChange = (e) => {
        const selectedDate = e.target.value;
        setToDate(selectedDate);
    };

    // here 4 parameter passing to help loading the infinte no:of data in index and fast loading 

    // Function to fetch table data and set state
    const fetchTableData = async () => {

        setData([]);
        try {
            const response = await fetchData('SaleApi', `/CustomerDetails/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
            if (response.responseCode === '1111') {
                setData(response.response_data);
            }
        } catch (error) {
            console.error('Error fetching Customer Details:', error);
            if (error.response && error.response.status === 401) {
                logout();
                history.replace(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
            }
        }

    };

    const handleDelete = async () => {
        deleteData('SaleApi', `${deleteItemId}`, token)
            .then((response) => {
                fetchTableData();
                setDeleteItemId(null);
                setConfirmDialogOpen(false);
            })
            .catch((error) => {
                console.error('Error deleting record:', error);
            });
    };

    const handleActivate = (itemId) => {
        activateData('SaleApi', `${itemId}`, token)
            .then(() => {
                fetchTableData();
            })
            .catch((error) => {
                console.error('Error activating record:', error);
            });
    };

    const openConfirmDialog = (itemId) => {
        setDeleteItemId(itemId);
        setConfirmDialogOpen(true);
    };

    const closeConfirmDialog = () => {
        setDeleteItemId(null);
        setConfirmDialogOpen(false);
    };

    function formatDate(dateString) {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        const formattedDate = new Date(dateString).toLocaleDateString('en-GB', options);
        return formattedDate;
    }

    const generateInvoice = async (saleId) => {
        try {
            const response = await fetchData('SaleApi', `/SalePrint/${saleId}/${branchId}`, token);
            const labelData = response.response_data;

            if (!labelData?.SalePrint || !labelData?.SalePrintDetails) {
                throw new Error("Invalid invoice data.");
            }

            const header = labelData.SalePrint;
            const items = labelData.SalePrintDetails;

            const doc = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: [80, 297]
            });

            let y = 6;

            doc.addImage(gender, 'PNG', 15, 2, 50, 20);
            y += 13;

            // Invoice Meta
            doc.setFontSize(6.5);
            const pageWidth = doc.internal.pageSize.getWidth();

            doc.setFont('helvetica', 'bold');
            doc.text('INVOICE', pageWidth / 2, y + 5, { align: 'center' });

            doc.setFont('helvetica', 'bold');
            const invNo = [header.BillPrefix, header.FinYear, header.BillNumber].filter(Boolean).join('/');
            doc.text(`Inv.No: ${invNo}`, 5, y + 8);
            doc.text(`Customer: ${header.CustomerName || ''}`, 5, y + 12);

            const billDate = header.BillDate || '';
            const createdDate = header.CreatedDate || '';
            //const formattedDate = billDate ? new Date(billDate).toLocaleDateString() : '';
            const formattedDate = billDate
                ? new Date(billDate).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric'
                }).replace(/ /g, '-')
                : '';

            const formattedTime = createdDate ? new Date(createdDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';

            doc.text(`Date: ${formattedDate}`, pageWidth - 5, y + 8, { align: 'right' });
            doc.text(`Time: ${formattedTime}`, pageWidth - 5, y + 12, { align: 'right' });

            y += 18;

            // Filter items
            const filteredItems = items.filter(item => (item.Quantity ?? 0) > 0 || (item.Total ?? 0) > 0);

            const itemRows = filteredItems.map(item => [
                { content: item.ItemName || '', styles: { cellWidth: 30, valign: 'top' } },
                { content: (item.Quantity ?? 0).toString(), styles: { halign: 'center' } },
                { content: (item.MRP ?? 0).toFixed(2), styles: { halign: 'right' } },
                { content: (item.Rate ?? 0).toFixed(2), styles: { halign: 'right' } },
                { content: (item.TotalAmount ?? 0).toFixed(2), styles: { halign: 'right' } }
            ]);

            const totalQuantity = filteredItems.reduce((sum, item) => sum + (item.Quantity ?? 0), 0);

            itemRows.push([
                { content: 'Total', styles: { halign: 'right', fontStyle: 'bold' } },
                { content: totalQuantity.toString(), styles: { halign: 'center', fontStyle: 'bold' } },
                { content: '', styles: {} },
                { content: '', styles: {} },
                { content: (header.GrandTotal ?? 0).toFixed(2), styles: { halign: 'right', fontStyle: 'bold' } }
            ]);

            autoTable(doc, {
                startY: y,
                margin: { left: 5 },
                head: [['NAME OF PRODUCT', 'QTY','MRP', 'RATE', 'TOTAL']],
                body: itemRows,
                styles: {
                    fontSize: 6.5,
                    textColor: [0, 0, 0],
                    cellPadding: { top: 0.5, bottom: 0.5, left: 1, right: 1 },
                    overflow: 'linebreak',
                    lineColor: [0, 0, 0], // ⬅️ border color black
                },
                theme: 'grid',
                headStyles: {
                    fillColor: [255, 255, 255],
                    textColor: [0, 0, 0],
                    fontStyle: 'bold',
                    lineColor: [0, 0, 0] // ⬅️ border color black for header
                },
                columnStyles: {
                    0: { cellWidth: 22 },
                    1: { cellWidth: 8 },
                    2: { cellWidth: 10 },
                    3: { cellWidth: 10 },
                    4: { cellWidth: 12 },
                },
            });


            let finalY = (doc.lastAutoTable?.finalY ?? y) + 3;

            // Tax summary
            const taxSummaryLines = [
                ['Taxable Amt', (header.TaxableAmount ?? 0).toFixed(2)],
                ['CGST Amt', (header.CGSTAmount ?? 0).toFixed(2)],
                ['SGST Amt', (header.SGSTAmount ?? 0).toFixed(2)],
                ['Round Off', (header.RoundOff ?? 0).toFixed(2)],
                ['Total Amt', (header.GrandTotal ?? 0).toFixed(2)],
            ];

            // Tax breakdown % table
            const taxGroups = {};
            filteredItems.forEach(item => {
                const gst = item.GSTRate ?? 0;
                if (!taxGroups[gst]) {
                    taxGroups[gst] = { amount: 0, cgst: 0, sgst: 0 };
                }
                taxGroups[gst].amount += item.TaxableAmount ?? 0;
                taxGroups[gst].cgst += item.CGSTAmount ?? 0;
                taxGroups[gst].sgst += item.SGSTAmount ?? 0;
            });

            const taxBreakdownRows = Object.entries(taxGroups).map(([percent, vals]) => ([
                percent,
                vals.amount.toFixed(2),
                vals.cgst.toFixed(2),
                vals.sgst.toFixed(2)
            ]));

            const totalRow = taxBreakdownRows.reduce((acc, row) => {
                for (let i = 1; i <= 3; i++) acc[i] += parseFloat(row[i]);
                return acc;
            }, ['Total', 0, 0, 0]).map((v, i) => i === 0 ? v : v.toFixed(2));

            taxBreakdownRows.push(totalRow);

            // Tax breakdown table with borders
            const leftX = 5;
            const rightX = 75;
            const rowHeight = 5;
            const colWidths = [8, 13, 13, 13];
            const colTitles = ['%', 'Amount', 'CGST', 'SGST'];

            doc.setFontSize(6.5);
            doc.setFont('helvetica', 'bold');
            doc.text('Tax Breakdown', leftX, finalY);

            let tableStartY = finalY + 4;
            let colX = leftX;

            colTitles.forEach((title, i) => {
                doc.rect(colX, tableStartY, colWidths[i], rowHeight);
                doc.text(title, colX + colWidths[i] / 2, tableStartY + 3.2, { align: 'center' });
                colX += colWidths[i];
            });

            doc.setFont('helvetica', 'normal');
            let currentY = tableStartY + rowHeight;

            taxBreakdownRows.forEach(row => {
                let rowX = leftX;
                row.forEach((cell, i) => {
                    doc.rect(rowX, currentY, colWidths[i], rowHeight);
                    doc.text(cell.toString(), rowX + colWidths[i] / 2, currentY + 3.2, { align: 'center' });
                    rowX += colWidths[i];
                });
                currentY += rowHeight;
            });

            let taxBreakdownBottomY = currentY;

            // // Right side: Tax Summary
            let summaryY = finalY;
            doc.setFont('helvetica', 'bold');
            taxSummaryLines.forEach(([label, value]) => {
                doc.text(`${label}: ${value}`, rightX, summaryY, { align: 'right' });
                summaryY += 4;
            });

            finalY = Math.max(taxBreakdownBottomY, summaryY) + 2;

            // // Net Total
            // doc.setFontSize(7.5);
            // doc.text(`INR ${(header.GrandTotal ?? 0).toFixed(2)}`, rightX, finalY, { align: 'right' });
            // // Total Discount
            // doc.setFontSize(7.5);
            // doc.text(`INR ${(header.DiscountAmount ?? 0).toFixed(2)}`, rightX, finalY, { align: 'right' });
            // Net Total
            doc.setFontSize(7.5);
            doc.setFont('helvetica', 'bold');
            doc.text(`INR: ${(header.GrandTotal ?? 0).toFixed(2)}`, rightX, finalY, { align: 'right' });

            // Discount
            finalY += 4; // move 4mm down (adjust spacing as needed)
            doc.setFontSize(7.5);
            doc.setFont('helvetica', 'normal');
            doc.text(`Discount: ${(header.DiscountAmount ?? 0).toFixed(2)}`, rightX, finalY, { align: 'right' });


            // Policies
            const marginX = 5;
            const textWidth = 70;
            currentY = finalY + 4;

            doc.setFontSize(5);
            doc.setFont("helvetica", "bold");

            const policy1 = "Exchange Policy for undergarments: For hygiene reason undergarments will not be exchanged or returned.";
            const policy2 = "Exchange Policy for Other items: Exchange is only applicable within 7 days from the billing date. Only items with intact original tags and a valid bill will be considered for exchange.";
            const policy3 = "Exchange Policy for Items: Exchange is only applicable only at respective stores.";

            const lines1 = doc.splitTextToSize(policy1, textWidth);
            doc.text(lines1, marginX, currentY);
            currentY += lines1.length * 2;

            const lines2 = doc.splitTextToSize(policy2, textWidth);
            doc.text(lines2, marginX, currentY);
            currentY += lines2.length * 2;

            const lines3 = doc.splitTextToSize(policy3, textWidth);
            doc.text(lines3, marginX, currentY);
            currentY += lines3.length * 2;

            finalY = currentY + 2; // SMALL gap before footer

            // Footer
            const centerX = pageWidth / 2;
            // doc.setFontSize(6);
            // doc.text('CUSTOMER CARE: 0484 4712 91 / 9995152525', centerX, finalY, { align: 'center' });
            // const addressLines = doc.splitTextToSize(header.AddressLine || '', 70); // 70 is max width in mm
            // addressLines.forEach((line, index) => {
            //     doc.text(line, centerX, finalY + 3 + index * 3, { align: 'center' }); // 4 is line height
            // });

            // make sure it's normal, not bold
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(6);

            doc.text('CUSTOMER CARE: 9995152525', centerX, finalY, { align: 'center' });

            const addressLines = doc.splitTextToSize(header.AddressLine || '', 70);
            addressLines.forEach((line, index) => {
                doc.text(line, centerX, finalY + 3 + index * 3, { align: 'center' });
            });

            doc.text(`GSTIN: ${header.CompanyGSTNumber || ''} `, centerX, finalY + 9, { align: 'center' });

            finalY += 12;
            doc.text(`Sales Person: ${header.SalesPersonName || ''}`, 5, finalY);
            doc.text(`User Name: ${header.UserName || ''}`, 5, finalY + 4);

            finalY += 8;
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(6.5);
            doc.text('THANK YOU - VISIT AGAIN', 40, finalY + 4, { align: 'center' });

            // Save PDF
            //doc.save(`invoice_${invNo || saleId}.pdf`);

            // Generate PDF as blob and open in new tab for printing
            const blob = doc.output('blob');
            const blobUrl = URL.createObjectURL(blob);
            const printWindow = window.open(blobUrl, '_blank');

            // Auto-trigger the print dialog once the PDF is loaded
            if (printWindow) {
                printWindow.onload = function () {
                    printWindow.focus();
                    printWindow.print();
                };
            }

        } catch (err) {
            console.error("Error generating invoice:", err);
        }
    };

    const AmountInWords = ({ amount }) => {
        const formattedAmount = parseFloat(amount).toFixed(2);
        const [rupees, decimal] = formattedAmount.split('.');

        return (
            <div>
                <h5>Rupees {NumberToWords(parseInt(rupees))} Only</h5>
                {decimal !== "00" && <p>{NumberToWords(parseInt(decimal))} Paise Only</p>}
            </div>
        );
    };

    return (
        <div className="w-full  mx-auto" style={{ width: '100%', marginTop: '10px' }}>
            <div className='w-full mb-2 flex'>
                <h6 className='block uppercase tracking-wide text-black productHead flex-grow linkcolor' style={{ fontFamily: 'Georgia, Cambria, "Times New Roman", Times, serif' }}>Sale </h6>
                <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
                    <input
                        className="shadow text-gray-700 leading-tight"
                        type="text"
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={handleSearch}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                        style={{
                            width: isFocused ? '270px' : '200px',
                            height: '30px',
                            border: '1px solid #ccc',
                            borderRadius: '15px',
                            padding: '0 20px',
                            transition: 'width 0.4s ease-in-out, border-color 0.3s ease-in-out',
                            outline: 'none',
                            borderColor: isFocused ? 'darkcyan' : '#ccc',
                            boxShadow: isFocused ? '0 4px 8px rgba(0, 0, 0, 0.2)' : 'none',
                            marginTop: '15px',
                        }}
                    />
                </div>
                <div style={{ marginRight: '2', maxWidth: '150px', marginLeft: '10px' }}>
                    <label className="block uppercase tracking-wide text-black text-xs font-bold mb-2" htmlFor="gstNumber" style={{ fontSize: '10px' }}>
                        From Date
                    </label>
                    <input
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline h-8"
                        type="date" value={fromDate} onChange={handleFromDateChange}
                        style={{ marginTop: '-10px', shadow: 'appearance-none', border: 'rounded', width: '100%', paddingY: '2', paddingX: '3', color: 'gray-700', leadingTight: true, focus: { color: 'gray-600', shadow: 'outline' } }}
                    />
                </div>
                <div style={{ marginRight: '2', maxWidth: '150px', marginLeft: '10px' }}>
                    <label className="block uppercase tracking-wide text-black text-xs font-bold mb-2" htmlFor="gstNumber" style={{ fontSize: '10px' }}>
                        To Date
                    </label>
                    <input
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline h-8"
                        type="date" value={toDate} onChange={handleToDateChange}
                        style={{ marginTop: '-10px', shadow: 'appearance-none', border: 'rounded', width: '100%', paddingY: '2', paddingX: '3', color: 'gray-700', leadingTight: true, focus: { color: 'gray-600', shadow: 'outline' } }}
                    />
                </div>
                <Button
                    style={{ background: 'linear-gradient(to right, #041815ff, #045157ff)', marginLeft: '8px', color: 'white', fontWeight: 'bolder', height: '30px', fontSize: '12px', textTransform: 'uppercase', marginTop: '20px', borderRadius: '25px' }}
                    onClick={fetchTableData}
                    className="linkcolor"
                >
                    Refresh
                </Button>

            </div>

            <div className="watermark">
                <CopyrightIcon /><b style={{ fontFamily: 'Script Bold Italic' }}>EFFTABS SOLUTIONS LLP</b>
            </div>
            <div className='w-full mb-2 flex' style={{ maxHeight: '520px', overflowY: 'auto' }}>
                <TableContainer component={Paper}>
                    <Table stickyHeader>
                        <TableHead sx={{ backgroundColor: '#58A5B3' }}>
                            <TableRow>
                                <TableCell
                                    style={{
                                        backgroundColor: '#27445D', borderBottom: '1px solid #F5F5F5', borderRight: '1px solid grey',
                                        borderRadius: '5px', color: 'white', width: '2%',
                                        fontWeight: '600', fontSize: '11px', border: '1px solid #ccc', textAlign: 'center', position: 'sticky',
                                        top: 0, zIndex: 10, whiteSpace: 'nowrap', padding: '3px', borderRadius: '4px', textTransform: 'uppercase'
                                    }}>
                                    SL NO
                                </TableCell>
                                <TableCell
                                    style={{
                                        backgroundColor: '#27445D', borderBottom: '1px solid #F5F5F5', borderRight: '1px solid grey',
                                        borderRadius: '5px', color: 'white', width: '7%',
                                        fontWeight: '600', fontSize: '11px', border: '1px solid #ccc', textAlign: 'center', position: 'sticky',
                                        top: 0, zIndex: 10, whiteSpace: 'nowrap', padding: '3px', borderRadius: '4px', textTransform: 'uppercase'
                                    }}>
                                    BILL DATE
                                </TableCell>
                                <TableCell
                                    style={{
                                        backgroundColor: '#27445D', borderBottom: '1px solid #F5F5F5', borderRight: '1px solid grey',
                                        borderRadius: '5px', color: 'white', width: '10%',
                                        fontWeight: '600', fontSize: '11px', border: '1px solid #ccc', textAlign: 'center', position: 'sticky',
                                        top: 0, zIndex: 10, whiteSpace: 'nowrap', padding: '3px', borderRadius: '4px', textTransform: 'uppercase'
                                    }}>
                                    BILL NUMBER
                                </TableCell>
                                <TableCell
                                    style={{
                                        backgroundColor: '#27445D', borderBottom: '1px solid #F5F5F5', borderRight: '1px solid grey',
                                        borderRadius: '5px', color: 'white', width: '15%',
                                        fontWeight: '600', fontSize: '11px', border: '1px solid #ccc', textAlign: 'center', position: 'sticky',
                                        top: 0, zIndex: 10, whiteSpace: 'nowrap', padding: '3px', borderRadius: '4px', textTransform: 'uppercase'
                                    }}>
                                    CUSTOMER
                                </TableCell>
                                <TableCell style={{
                                    backgroundColor: '#27445D', borderBottom: '1px solid #F5F5F5', borderRight: '1px solid grey',
                                    borderRadius: '5px', color: 'white', width: '13%',
                                    fontWeight: '600', fontSize: '11px', border: '1px solid #ccc', textAlign: 'center', position: 'sticky',
                                    top: 0, zIndex: 10, whiteSpace: 'nowrap', padding: '3px', borderRadius: '4px', textTransform: 'uppercase'
                                }}>
                                    PHONE NO
                                </TableCell>
                                <TableCell style={{
                                    backgroundColor: '#27445D', borderBottom: '1px solid #F5F5F5', borderRight: '1px solid grey',
                                    borderRadius: '5px', color: 'white', width: '7%',
                                    fontWeight: '600', fontSize: '11px', border: '1px solid #ccc', textAlign: 'center', position: 'sticky',
                                    top: 0, zIndex: 10, whiteSpace: 'nowrap', padding: '3px', borderRadius: '4px', textTransform: 'uppercase'
                                }}>
                                    AMOUNT
                                </TableCell>
                                <TableCell style={{
                                    backgroundColor: '#27445D', borderBottom: '1px solid #F5F5F5', borderRight: '1px solid grey',
                                    borderRadius: '5px', color: 'white', width: '7%',
                                    fontWeight: '600', fontSize: '11px', border: '1px solid #ccc', textAlign: 'center', position: 'sticky',
                                    top: 0, zIndex: 10, whiteSpace: 'nowrap', padding: '3px', borderRadius: '4px', textTransform: 'uppercase'
                                }}>
                                    GST
                                </TableCell>
                                <TableCell style={{
                                    backgroundColor: '#27445D', borderBottom: '1px solid #F5F5F5', borderRight: '1px solid grey',
                                    borderRadius: '5px', color: 'white', width: '7%',
                                    fontWeight: '600', fontSize: '11px', border: '1px solid #ccc', textAlign: 'center', position: 'sticky',
                                    top: 0, zIndex: 10, whiteSpace: 'nowrap', padding: '3px', borderRadius: '4px', textTransform: 'uppercase'
                                }}>
                                    TOTAL
                                </TableCell>
                                <TableCell style={{
                                    backgroundColor: '#27445D', borderBottom: '1px solid #F5F5F5', borderRight: '1px solid grey',
                                    borderRadius: '5px', color: 'white', width: '7%',
                                    fontWeight: '600', fontSize: '11px', border: '1px solid #ccc', textAlign: 'center', position: 'sticky',
                                    top: 0, zIndex: 10, whiteSpace: 'nowrap', padding: '3px', borderRadius: '4px', textTransform: 'uppercase'
                                }}>

                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody sx={{ backgroundColor: 'whitesmoke' }}>
                            {filteredData.map((item, index) => (
                                <TableRow key={item.id} className={item.IsActive ? '' : 'inactiveRow'}
                                    sx={{
                                        '&:hover': {
                                            backgroundColor: '#dff6f4ff', // Change color on hover
                                            cursor: 'pointer'
                                        },
                                    }}
                                >
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', height: '20px', padding: '3px', width: '3%', fontSize: '12px' }}>{index + 1}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', height: '20px', padding: '3px', width: '7%', fontSize: '12px' }}>{formatDate(item.BillDate)}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', height: '20px', padding: '3px', width: '10%', fontSize: '12px' }}>{item.BillPrefix}/{item.FinYear}/{item.BillNumber}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', height: '20px', padding: '3px', width: '15%', fontSize: '12px' }}>{item.CustomerName}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', height: '20px', padding: '3px', width: '13%', fontSize: '12px' }}>{item.ContactNumber}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', height: '20px', padding: '3px', width: '10%', textAlign: 'right', fontSize: '12px' }}>{item.TaxableAmount ? item.TaxableAmount.toFixed(2) : "0.00"} </TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', height: '20px', padding: '3px', width: '7%', textAlign: 'right', fontSize: '12px' }}>
                                        {(
                                            (item.CGSTAmount || 0) +
                                            (item.SGSTAmount || 0) +
                                            (item.IGSTAmount || 0)
                                        ).toFixed(2)}
                                    </TableCell>

                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', height: '20px', padding: '3px', width: '7%', textAlign: 'right', fontSize: '12px' }}>{item.GrandTotal ? item.GrandTotal.toFixed(2) : "0.00"}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', height: '20px', padding: '3px', textAlign: 'right', width: '10%', fontSize: '12px' }}>

                                        <IconButton component={Link} to={`/NewSaleDetails/${item.Id}`}>
                                            <Info style={{ color: 'coral', fontSize: '19px' }} />
                                        </IconButton>

                                        {/* <IconButton onClick={() => handlePrint(item.Id)}>
                                            <PrintIcon style={{ color: 'darkgoldenrod', fontSize: '22px' }} />
                                        </IconButton> */}

                                        <IconButton onClick={() => generateInvoice(item.Id)}>
                                            <PictureAsPdfIcon style={{ color: 'crimson', fontSize: '19px' }} />
                                        </IconButton>



                                        {hasEditMasterPermission && item.IsActive && (
                                            <IconButton component={Link} to={`/NewSaleEdit/${item.Id}`} color="primary">
                                                <EditIcon style={{ fontSize: '19px' }} />
                                            </IconButton>
                                        )}

                                        {hasDeleteMasterPermission && (
                                            <IconButton onClick={() => (item.IsActive ? openConfirmDialog(item.Id) : handleActivate(item.Id))} color="error">
                                                {item.IsActive ? <DeleteIcon style={{ fontSize: '19px' }} /> : <ActivateIcon style={{ color: 'darkolivegreen', fontSize: '19px' }} />}
                                            </IconButton>
                                        )}

                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                        <Dialog
                            open={confirmDialogOpen}
                            onClose={closeConfirmDialog}
                            aria-labelledby="alert-dialog-title"
                            aria-describedby="alert-dialog-description"
                        >
                            <DialogTitle id="alert-dialog-title">{"Delete Record?"}</DialogTitle>
                            <DialogContent>
                                <DialogContentText id="alert-dialog-description">
                                    Are you sure you want to delete this record?
                                </DialogContentText>
                            </DialogContent>

                            <DialogActions>
                                <button
                                    onClick={closeConfirmDialog}
                                    style={{
                                        backgroundColor: '#dc3545',
                                        color: '#ffffff',
                                        border: '1px solid #dc3545',
                                        borderRadius: '4px',
                                        padding: '8px 16px',
                                        cursor: 'pointer',
                                        marginRight: '8px',
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDelete}
                                    style={{
                                        backgroundColor: '#28a745',
                                        color: '#ffffff',
                                        border: '1px solid #28a745',
                                        borderRadius: '4px',
                                        padding: '8px 16px',
                                        cursor: 'pointer',
                                    }}
                                >
                                    Confirm
                                </button>
                            </DialogActions>

                        </Dialog>

                    </Table>
                </TableContainer>
            </div>
        </div>

    );
}

export default NewSaleIndex;

